import { useCallback, type RefObject } from 'react';

import type { SpinButtonTextSelection } from '../types.js';

/**
 * This hook provides text selection and cursor utility functions.
 * Because the SpinButton is a contenteditable span, this is not provided natively.
 */
export const useSelection = (
  ref: RefObject<HTMLElement | null> | undefined,
) => {
  /** Clear current text selection */
  const deselectAll = useCallback(() => {
    window.getSelection()?.removeAllRanges();
  }, []);

  /** Select whole spinbutton text content */
  const selectAll = useCallback(() => {
    if (!ref?.current) {
      return;
    }

    const el = ref.current;
    const selection = window.getSelection();
    const textNode = el.childNodes[0];

    // If element lacks a textNode, there is nothing to select.
    if (!selection || !textNode) {
      return;
    }

    const newRange = document.createRange();
    newRange.setStart(textNode, 0);
    newRange.setEnd(textNode, textNode.textContent?.length ?? 0);

    selection.removeAllRanges();
    selection.addRange(newRange);
  }, [ref]);

  /** Returns the current text selection of the spin button. */
  const getSelection = useCallback((): SpinButtonTextSelection | undefined => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      return undefined;
    }

    const range = selection.getRangeAt(0);

    // return undefined if the selected range is outside of the spinbutton
    if (
      range.startContainer !== range.endContainer &&
      range.startContainer !== ref?.current
    ) {
      return undefined;
    }

    return { start: range.startOffset, end: range.endOffset, rtl: false };
    // Ignore ref dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Set the text selection within the spin button */
  const setSelection = useCallback(
    (selection: Partial<SpinButtonTextSelection>) => {
      const textNode = ref?.current?.childNodes[0];
      if (!textNode) {
        return;
      }

      const length = textNode.textContent?.length ?? 0;
      const { rtl = false } = selection;
      let { start = 0, end = length } = selection;

      if (rtl) {
        start = length - start;
        end = length - end;
      }

      // fix boundaries
      start = Math.min(length, start);
      end = Math.min(length, end);

      // create new selection and overwrite an existing one
      const newRange = document.createRange();
      newRange.setStart(textNode, start);
      newRange.setEnd(textNode, end);

      deselectAll();
      window.getSelection()?.addRange(newRange);
    },
    [deselectAll, ref],
  );

  return {
    selectAll,
    deselectAll,
    getSelection,
    setSelection,
  };
};
